import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle, Warning, UserPlus, EnvelopeSimple } from "@phosphor-icons/react";

function OrderSummary({ order }) {
  const rows = [
    ["Item", `${order.product_name} × ${order.quantity}`],
    ["Price", `${formatMoney(order.unit_price)} ${order.price_tier === "member" ? "member price" : "MSRP"}`],
    ["Subtotal", formatMoney(order.amount_subtotal)],
    ["Shipping", `${order.shipping_method || "—"} · ${formatMoney(order.amount_shipping)}`],
    ["Tax", formatMoney(order.amount_tax)],
  ];
  return (
    <div className="surface p-6 text-left" data-testid="shop-order-summary">
      <div className="font-mono text-[10px] text-[#00D4FF] tracking-widest uppercase mb-3">Order #{order.order_number}</div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between font-plex text-sm py-1"><span className="text-[#9CA3AF]">{k}</span><span className="text-white">{v}</span></div>
      ))}
      <div className="flex justify-between border-t border-[#262626] mt-3 pt-3 font-mono font-bold">
        <span className="text-white">TOTAL</span><span className="text-[#00D4FF]" data-testid="shop-order-total">{formatMoney(order.amount_total)}</span>
      </div>
    </div>
  );
}

function CreateAccountCta({ sessionId, email }) {
  const [state, setState] = useState("idle");
  const create = async () => {
    setState("busy");
    try { await api.post(`/shop/orders/${sessionId}/create-account`); setState("done"); }
    catch (e) { toast.error(formatApiError(e)); setState("idle"); }
  };
  if (state === "done") {
    return (
      <div className="surface p-6 text-left flex gap-3" data-testid="shop-account-created">
        <EnvelopeSimple size={24} weight="bold" className="text-[#00D4FF] shrink-0" />
        <p className="font-plex text-sm text-[#9CA3AF]">Account created. We sent a link to <strong className="text-white">{email}</strong> to set your password.</p>
      </div>
    );
  }
  return (
    <div className="surface p-6 text-left" data-testid="shop-create-account-cta">
      <div className="font-chivo font-bold text-white uppercase">Save your order history</div>
      <p className="font-plex text-sm text-[#9CA3AF] mt-1 mb-4">Create a free account for {email} in one click to track orders and check out faster next time.</p>
      <button onClick={create} disabled={state === "busy"} className="btn-primary" data-testid="shop-create-account-btn">
        <UserPlus size={14} weight="bold" /> {state === "busy" ? "Creating..." : "Create my free account"}
      </button>
    </div>
  );
}

export default function ShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) { setStatus("missing"); return; }
    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (cancelled) return;
      if (attempt >= 15) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/shop/orders/status/${sessionId}`);
        if (["paid", "shipped", "delivered"].includes(data.status)) { setOrder(data); setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (e) { if (e?.response?.status === 404) { setStatus("missing"); return; } }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-20 space-y-6" data-testid="shop-success">
        {status === "checking" && <div className="font-mono text-[#00D4FF] tracking-widest text-center">[CONFIRMING PAYMENT]<span className="cursor-blink">_</span></div>}
        {status === "paid" && order && (
          <>
            <div className="text-center">
              <CheckCircle size={64} weight="fill" className="text-[#00D4FF] mx-auto mb-4" />
              <h1 className="font-chivo font-black text-4xl text-white uppercase" data-testid="shop-success-headline">Order Confirmed</h1>
              <p className="font-plex text-[#9CA3AF] mt-2">A receipt is on its way to {order.customer_email}. We'll email tracking once it ships.</p>
            </div>
            <OrderSummary order={order} />
            {!user && order.is_guest && !order.account_exists && <CreateAccountCta sessionId={sessionId} email={order.customer_email} />}
            <div className="text-center"><Link to="/shop" className="btn-secondary" data-testid="shop-success-continue">Continue Shopping</Link></div>
          </>
        )}
        {["timeout", "expired", "missing"].includes(status) && (
          <div className="text-center" data-testid="shop-success-error">
            <Warning size={56} weight="fill" className="text-[#FF5722] mx-auto mb-4" />
            <h1 className="font-chivo font-black text-3xl text-white uppercase">{status === "expired" ? "Checkout expired" : "We couldn't confirm this order"}</h1>
            <p className="font-plex text-[#9CA3AF] mt-2 mb-6">If you were charged, your receipt email is your confirmation — contact us and we'll sort it out.</p>
            <Link to="/shop" className="btn-primary">Back to Shop</Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
